/**
 * /claim?token=… — claim a keyless payment-link account.
 *
 * A keyless account (created via POST /api/v1/links or /tip-jars) is owned by
 * whoever holds its `manage_token` capability secret. This screen binds that
 * account to a real login so the holder gets the full dashboard:
 *
 *   1. Read the token from the URL (stash it in sessionStorage so it survives
 *      the sign-in round-trip, since AuthGate's redirect only keeps the path).
 *   2. If signed out, bounce to /login?redirect=/claim and come back.
 *   3. Signed in → POST /api/v1/links/claim → bind + mark claimed → dashboard.
 *
 * /claim is a public route (handles its own auth) so the token isn't lost.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { getStoredSession } from "../lib/session";
import { claimLink, ApiError } from "../lib/api";

const PENDING_KEY = "shieldz.pendingClaim";
const ACCENT = "#16a34a";

type State =
  | { k: "working" }
  | { k: "done" }
  | { k: "error"; title: string; detail: string };

export function ClaimScreen() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string };
  const [state, setState] = useState<State>({ k: "working" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    void (async () => {
      // Token from the URL, or recovered from before a sign-in round-trip.
      let token = typeof search.token === "string" ? search.token.trim() : "";
      if (token) {
        try {
          sessionStorage.setItem(PENDING_KEY, token);
        } catch {
          /* private mode */
        }
      } else {
        try {
          token = sessionStorage.getItem(PENDING_KEY) ?? "";
        } catch {
          /* private mode */
        }
      }

      if (!token) {
        setState({
          k: "error",
          title: "Invalid claim link",
          detail: "This link is missing its token. Open the exact link from your email.",
        });
        return;
      }

      // Need a signed-in account to bind the link to.
      if (!getStoredSession()) {
        void navigate({ to: "/login", search: { redirect: "/claim" } });
        return;
      }

      try {
        await claimLink(token);
        try {
          sessionStorage.removeItem(PENDING_KEY);
        } catch {
          /* swallow */
        }
        setState({ k: "done" });
        setTimeout(() => void navigate({ to: "/" }), 1400);
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.code === "unauthenticated")) {
          void navigate({ to: "/login", search: { redirect: "/claim" } });
          return;
        }
        const code = e instanceof ApiError ? e.code : "";
        const map: Record<string, string> = {
          invalid_token: "This claim link is invalid or has expired.",
          already_claimed: "This account has already been claimed by a different login.",
          account_has_merchant:
            "This login already has a Shieldz dashboard. Sign in with a different wallet or email to claim this account.",
          wallet_or_email_required: "Sign in with a wallet or email to claim this account.",
        };
        setState({
          k: "error",
          title: "Couldn't claim this account",
          detail: map[code] ?? (e instanceof ApiError ? e.message : "Something went wrong. Try again."),
        });
      }
    })();
  }, [navigate, search.token]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg, #fafbfc)",
        fontFamily: "Geist, system-ui, sans-serif",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          border: "1px solid #e6e9ee",
          borderRadius: 16,
          padding: "32px 28px",
          textAlign: "center",
          boxShadow: "0 10px 40px -20px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 12,
            background: ACCENT,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: 24,
            margin: "0 auto 18px",
          }}
        >
          S
        </div>

        {state.k === "working" && (
          <>
            <h1 style={{ fontSize: 19, margin: "0 0 6px", color: "#0c1116" }}>Claiming your account…</h1>
            <p style={{ fontSize: 14, color: "#4a5560", margin: 0 }}>
              Linking this payment account to your login.
            </p>
          </>
        )}

        {state.k === "done" && (
          <>
            <h1 style={{ fontSize: 19, margin: "0 0 6px", color: "#0c1116" }}>Account claimed ✓</h1>
            <p style={{ fontSize: 14, color: "#4a5560", margin: "0 0 18px" }}>
              This payment account is now yours. Taking you to your dashboard…
            </p>
            <button
              onClick={() => void navigate({ to: "/" })}
              style={{
                minHeight: 46,
                padding: "0 22px",
                borderRadius: 12,
                background: ACCENT,
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Go to dashboard
            </button>
          </>
        )}

        {state.k === "error" && (
          <>
            <h1 style={{ fontSize: 19, margin: "0 0 6px", color: "#0c1116" }}>{state.title}</h1>
            <p style={{ fontSize: 14, color: "#4a5560", margin: "0 0 18px" }}>{state.detail}</p>
            <button
              onClick={() => void navigate({ to: "/" })}
              style={{
                minHeight: 46,
                padding: "0 22px",
                borderRadius: 12,
                background: "#141416",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Go to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
